import React from "react";

import { Button, Label } from "@busbud/horizon";
import type * as UDM from "@busbud/int-schemas/udm";

import { useAppContext } from "@app/helpers/hooks";
import type { TranslatorService } from "@app/services/translator";
import type { CurrencyCode } from "@app/types/currency";
import type { PassengerSeatSelectionInstance } from "@app/types/seat-selection-types";

import type { SeatPrices } from "./seat-price";

import { SelectedSeatsList } from "./selected-seats-list";
import { SummaryPrice } from "./summary-price";
import Icon from "../../../../../components/icon";

const getSelectButtonText = (
  translator: TranslatorService,
  is_external: boolean
) =>
  is_external
    ? translator.t("!results.select-referral-departure")
    : translator.t("!checkout.seat-selection.confirm-and-continue");

export interface SummaryInlineProps {
  currency: CurrencyCode;
  passengers: PassengerSeatSelectionInstance[];
  selected_seats: UDM.SeatCell[];
  onConfirm: () => void;
  gender_rule_warning?: React.ReactNode;
  prices: SeatPrices;
  segment_id: string;
  is_external: boolean;
}

export const SummaryInline: React.FC<SummaryInlineProps> = props => {
  const { translator } = useAppContext();
  const {
    currency,
    passengers,
    selected_seats,
    gender_rule_warning,
    prices,
    segment_id,
    is_external
  } = props;

  const warning_exists = !!gender_rule_warning;
  const not_all_passengers_have_seats =
    selected_seats.length !== passengers.length;

  return (
    <div className="flex flex-col gap-400">
      {gender_rule_warning}
      <div className="flex flex-col gap-050">
        <Label size="md" fontWeight="bold" as="p">
          {translator.t("!seat-selection.selected-seats")}
        </Label>
        <SelectedSeatsList seats={selected_seats} passengers={passengers} />
      </div>
      <SummaryPrice
        currency={currency}
        passengers={passengers}
        selected_seats={selected_seats}
        segment_id={segment_id}
        prices={prices}
      />
      <Button
        size="md"
        onClick={props.onConfirm}
        variant="contained"
        isDisabled={not_all_passengers_have_seats || warning_exists}
        data-cy="select-seat-departure"
      >
        {getSelectButtonText(translator, is_external)}
        {!!is_external && " "}
        {!!is_external && (
          <Icon name="external-link-busbud" width={14} height={14} />
        )}
      </Button>
    </div>
  );
};
